import { tokenService } from '@features/authentication/services/tokenService';
import { ensureFreshAccessTokenForStream } from '@shared/api/ensureFreshAccessTokenForStream';
import type { Notification, NotificationCounts } from '../types/notification.types';

/**
 * N5 (86d3w2kg2) — the notification SSE transport. Cloned from `quotationEventStream.ts`, which is
 * the house shape for this: `?access_token=` in the URL (EventSource accepts no headers), our own
 * 1s→30s backoff rather than the browser's unobservable one, and a pre-reconnect token refresh.
 *
 * ⚠️ THE REFRESH IS NOT OPTIONAL (E7 item 4). The JWT rides in the URL and is captured when the URL
 * is built, so a reconnect after the 15-minute access-token expiry re-presents a dead token and is
 * rejected again — for ever. A clone that omits it loops 401s after any idle reconnect and looks like
 * a server fault.
 */

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL as string;

/**
 * ⛔ Fixed. Not `/api/notifications/stream`, which appears in the handoff as shorthand — N2 owns this
 * endpoint and it is also the fifth clause in `Program.cs`'s hardcoded query-token allow-list. A
 * mismatch here presents as a stream that connects and then 401s.
 */
const STREAM_PATH = '/notifications/events';
const MIN_BACKOFF_MS = 1000;
const MAX_BACKOFF_MS = 30000;

/** The three frame types N2's bus publishes. Literal strings, matching the server's factories. */
export type NotificationFrame =
  | { type: 'upsert'; notification: Notification; counts: NotificationCounts }
  | { type: 'counts_changed'; counts: NotificationCounts }
  | { type: 'notifications.changed'; notifications: Notification[]; counts: NotificationCounts };

export type ConnectionState = 'connecting' | 'open' | 'reconnecting';

export interface NotificationStreamHandlers {
  onFrame: (frame: NotificationFrame) => void;
  onConnectionStateChange?: (state: ConnectionState) => void;
  /** A gap just closed — the consumer should catch up on whatever it missed. Silently. */
  onReconnect?: () => void;
}

export interface NotificationStreamHandle {
  close: () => void;
}

export function openNotificationEventStream(
  handlers: NotificationStreamHandlers,
): NotificationStreamHandle {
  let source: EventSource | null = null;
  let backoff = MIN_BACKOFF_MS;
  let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  let closed = false;
  let hasConnectedOnce = false;

  function emitState(next: ConnectionState) {
    handlers.onConnectionStateChange?.(next);
  }

  function buildUrl(): string {
    const accessToken = tokenService.getAccessToken();
    const params = new URLSearchParams();
    if (accessToken) params.set('access_token', accessToken);
    return `${API_BASE_URL}${STREAM_PATH}?${params.toString()}`;
  }

  function connect() {
    if (closed) return;
    emitState(hasConnectedOnce ? 'reconnecting' : 'connecting');

    try {
      source = new EventSource(buildUrl());
    } catch {
      scheduleReconnect();
      return;
    }

    source.onopen = () => {
      backoff = MIN_BACKOFF_MS;
      emitState('open');
      // Only on a RE-connect: the first open has nothing to catch up on, and firing here would make
      // every page load pay for a redundant whole-feed merge.
      if (hasConnectedOnce) handlers.onReconnect?.();
      hasConnectedOnce = true;
    };

    source.onmessage = (msg) => {
      if (!msg.data) return;
      try {
        handlers.onFrame(JSON.parse(msg.data) as NotificationFrame);
      } catch (err) {
        // A garbled payload costs one frame; tearing the stream down over it would cost every
        // subsequent one.
        console.warn('Failed to parse notification frame:', err, msg.data);
      }
    };

    source.onerror = () => {
      // EventSource reconnects on its own, but with unobservable timing and no state events. Closing
      // and rescheduling ourselves is what makes the connection state renderable AND lets the retry
      // pass through the token refresh below.
      source?.close();
      source = null;
      scheduleReconnect();
    };
  }

  function scheduleReconnect() {
    if (closed) return;
    emitState('reconnecting');
    if (reconnectTimer) clearTimeout(reconnectTimer);

    reconnectTimer = setTimeout(async () => {
      reconnectTimer = null;
      // See the ⚠️ at the top of this file. Never throws; `closed` is re-checked because the await
      // yields and the consumer may have unmounted meanwhile.
      await ensureFreshAccessTokenForStream();
      if (closed) return;
      connect();
    }, backoff);

    backoff = Math.min(backoff * 2, MAX_BACKOFF_MS);
  }

  connect();

  return {
    close() {
      closed = true;
      if (reconnectTimer) {
        clearTimeout(reconnectTimer);
        reconnectTimer = null;
      }
      if (source) {
        source.close();
        source = null;
      }
    },
  };
}
